import React from "react";
import ScriptBuilder from "./script_builder";
import { pluralize } from "./form_helpers";
import {
  ContentContainer,
  PageHeader,
  SectionHeader,
  astryx,
  type IScriptTemplate
} from "@shadowcue/ui";

export interface ScriptTemplateFormProps {
  values: IScriptTemplate;
  onChange: (values: IScriptTemplate) => void;
  errors: Record<string, string>;
  onClearError: (key: string) => void;
  loading?: boolean;
  isEditing?: boolean;
  readonly?: boolean;
  /** Rendered at the end of the page header row (e.g. a create-mode toggle). */
  headerAction?: React.ReactNode;
}

const ScriptTemplateForm = ({
  values,
  onChange,
  errors,
  onClearError,
  loading = false,
  isEditing = false,
  readonly = false,
  headerAction,
}: ScriptTemplateFormProps) => {
  const sectionCount = values.scriptTemplateSections.length;
  const questionCount = values.scriptTemplateSections.reduce(
    (total, section) => total + section.scriptTemplateQuestions.length,
    0,
  );

  return (
    <ContentContainer loading={loading}>
      <PageHeader
        title={isEditing ? "Edit script template" : "New script template"}
        description={
          isEditing
            ? "Update the details and interview script."
            : "Name the role, then build the interview section by section."
        }
        action={headerAction}
      />

      <astryx.VStack gap={8}>
        <astryx.VStack gap={4}>
          <SectionHeader title="Details" />
          <astryx.VStack gap={4}>
            <astryx.TextInput
              label="Script name"
              isRequired
              value={values.name}
              onChange={(value) => {
                onClearError("name");
                onChange({ ...values, name: value });
              }}
              placeholder="e.g. Senior Backend Engineer — Rails"
              width="100%"
              data-field-key="name"
              isDisabled={readonly}
              status={errors["name"] ? { type: "error", message: errors["name"] } : undefined}
            />
            <astryx.TextArea
              label="Job description"
              value={values.description ?? ""}
              onChange={(value) => onChange({ ...values, description: value })}
              placeholder="Briefly describe the role or interview focus for this script."
              rows={3}
              width="100%"
              isDisabled={readonly}
            />
            <astryx.TextArea
              label="Notes"
              labelTooltip="Context for the AI — not shown to the candidate."
              value={values.notes ?? ""}
              onChange={(value) => onChange({ ...values, notes: value })}
              placeholder="Notes from the hiring manager about what to look for in a strong candidate."
              rows={3}
              width="100%"
              isDisabled={readonly}
            />
          </astryx.VStack>
        </astryx.VStack>

        <astryx.VStack gap={4}>
          <SectionHeader
            title="Interview script"
            action={
              <astryx.Badge label={`${pluralize(sectionCount, "section")} · ${pluralize(questionCount, "question")}`} />
            }
          />
          <ScriptBuilder
            values={values}
            onChange={onChange}
            errors={errors}
            onClearError={onClearError}
            readonly={readonly}
          />
        </astryx.VStack>
      </astryx.VStack>
    </ContentContainer>
  );
};

export default ScriptTemplateForm;
